import { ChangeEvent, Fragment, MouseEvent, ReactNode, useState, useRef, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material';
import { AddRounded as AddIcon, MoreHoriz as MoreIcon } from '@mui/icons-material';
import {
  Menu,
  useNameField,
  generateTestIds,
  IValidationResult,
  IMenuAction,
  StyledValidationTooltip,
  ValidationErrorMessage,
  ValidationLCTypes,
  useAssistantLoadingStore,
} from '@diana/core';
import { NameTextField } from './FlowExplorer.styles';
import { IFlowExplorerMenuAction, TFlowExplorerNameTextFieldProps } from './FlowExplorer.types';
import { useFlowBuilder2PageStore } from '../../Store/useFlowBuilder2PageStore';
import { isEqual } from 'lodash';

interface INodeHeaderRenderContentProvided {
  isHovered: boolean;
  isRenaming: boolean;
  isHighlighted: boolean;
}

export interface INodeHeaderProps {
  label?: string;
  labelVariant?: TypographyProps['variant'];
  untitledLabel?: string;
  addItemTooltip?: ReactNode;
  isMuted?: boolean;
  isSelected?: boolean;
  isActionsLoading?: boolean;
  highlightOnHover?: boolean;
  showContextMenu?: boolean;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  onChangeName?: (e: ChangeEvent<HTMLInputElement>) => void;
  onAddItem?: () => Promise<void>;
  canRename?: boolean;
  actions?: IFlowExplorerMenuAction[];
  validationResult?: IValidationResult | null;
  renderNameTextField?: (props: TFlowExplorerNameTextFieldProps) => ReactNode;
  renderStartContent?: (provided: INodeHeaderRenderContentProvided) => ReactNode;
  renderEndContent?: (provided: INodeHeaderRenderContentProvided) => ReactNode;
}

export const NodeHeaderTestIds = generateTestIds('NodeHeader', [
  'Root',
  'NameContainer',
  'AddItemButton',
  'MenuButton',
  'RenameMenuAction',
  'NameTextField',
]);

export const NodeHeader = (props: INodeHeaderProps) => {
  const {
    label,
    labelVariant,
    untitledLabel,
    addItemTooltip,
    onClick,
    onChangeName,
    onAddItem,
    canRename,
    isMuted,
    isSelected,
    showContextMenu = true,
    isActionsLoading,
    highlightOnHover,
    actions,
    validationResult,
    renderNameTextField,
    renderStartContent,
    renderEndContent,
  } = props;

  const { palette } = useTheme();

  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);
  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);
  const disableEditing = hasAnyPendingStatus || isChatLoading;

  const { mutate: addItem, isPending: isAddingItem } = useMutation({
    mutationFn: async () => {
      if (onAddItem) await onAddItem();
    },
  });

  const { mutate: executeAction, isPending: isExecutingAction } = useMutation({
    mutationFn: (executeAction: () => Promise<void>) => executeAction(),
  });

  const isHighlighted = isSelected || (!!highlightOnHover && isHovered);
  const isError = validationResult?.type === 'error';
  const errorMessage = isError ? validationResult.message : '';
  const disabled = isExecutingAction || !canRename;

  const renameField = () => {
    setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const length = el.value.length;
        el.setSelectionRange(length, length);
      }
    }, 0);
  };

  const onDoubleClick = () => {
    if (canRename) {
      renameField();
    }
  };

  const {
    onClick: handleClick,
    onDoubleClick: handleLabelDoubleClick,
    onKeyDown,
    onBlur,
    onFocus,
    isRenaming,
    isFocused,
  } = useNameField({
    onClick,
    onDoubleClick,
    isError,
  });

  const menuActions = useMemo(() => {
    const items: IMenuAction[] = [];

    if (renderNameTextField || onChangeName) {
      items.push({
        title: 'Rename',
        disabled: !canRename,
        testId: NodeHeaderTestIds.RenameMenuAction,
        onClick: ({ close }) => {
          close();
          setTimeout(() => {
            onFocus();
            renameField();
          });
        },
      });
    }

    if (actions) {
      for (const { execute, ...action } of actions) {
        items.push({
          ...action,
          onClick: ({ close }) => {
            close();
            executeAction(execute);
          },
        });
      }
    }

    return items;
  }, [onChangeName, onFocus, renderNameTextField, canRename, actions, executeAction]);

  const menuActionsLoading = useMemo(() => menuActions.some((a) => a.loading), [menuActions]);

  const nodeName = useMemo(() => {
    if (isRenaming) {
      return (
        <Fragment>
          {renderNameTextField ? (
            renderNameTextField({
              value: label,
              onChange: onChangeName,
              onFocus,
              onBlur,
              onKeyDown,
              disabled,
              inputRef,
            })
          ) : (
            <NameTextField
              data-testid={NodeHeaderTestIds.NameTextField}
              value={label}
              onChange={onChangeName}
              disabled={disabled}
              onFocus={onFocus}
              onBlur={onBlur}
              fullWidth
              inputRef={inputRef}
            />
          )}
        </Fragment>
      );
    }

    if (label)
      return (
        <Typography
          title={label}
          variant={labelVariant}
          fontSize="1em"
          color={isMuted ? 'text.disabled' : undefined}
          textOverflow="ellipsis"
          overflow="hidden"
          style={{ marginLeft: '4px' }}
        >
          {label}
        </Typography>
      );

    return (
      <Typography
        variant={labelVariant}
        fontSize="1em"
        fontWeight={500}
        color={isMuted ? 'text.disabled' : 'text.placeholder'}
        style={{ marginLeft: '4px' }}
      >
        {untitledLabel || 'Untitled'}
      </Typography>
    );
  }, [
    labelVariant,
    label,
    isMuted,
    untitledLabel,
    renderNameTextField,
    onChangeName,
    onFocus,
    onBlur,
    onKeyDown,
    disabled,
    isRenaming,
  ]);

  const addItemIcon = useMemo(() => {
    if (isAddingItem) return <CircularProgress size="1em" />;
    if (onAddItem && (isHovered || isFocused))
      return (
        <Tooltip title={addItemTooltip}>
          <IconButton
            data-testid={NodeHeaderTestIds.AddItemButton}
            size="small"
            style={{ padding: 0 }}
            disabled={isExecutingAction}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              addItem();
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      );
  }, [isAddingItem, onAddItem, isHovered, isFocused, addItemTooltip, isExecutingAction, addItem]);

  const moreIconMenu = useMemo(() => {
    if (isActionsLoading || isExecutingAction || menuActionsLoading)
      return <CircularProgress size="1em" />;
    if (showContextMenu && menuActions.length > 0 && (isHovered || menuAnchor || isFocused))
      return (
        <IconButton
          data-testid={NodeHeaderTestIds.MenuButton}
          size="small"
          style={{ padding: 0 }}
          disabled={isExecutingAction}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
        >
          <MoreIcon />
        </IconButton>
      );
  }, [
    showContextMenu,
    isActionsLoading,
    isExecutingAction,
    menuActionsLoading,
    menuActions.length,
    isHovered,
    isFocused,
    menuAnchor,
    setMenuAnchor,
  ]);

  const backgroundColor = isSelected
    ? !!highlightOnHover && isHovered
      ? palette.action.selected
      : palette.action.hover
    : !!highlightOnHover && isHovered
    ? palette.action.hover
    : 'transparent';

  return (
    <Fragment>
      <Stack
        data-testid={NodeHeaderTestIds.Root}
        aria-selected={isSelected}
        pr={1}
        pl={0.5}
        py={0}
        justifyContent="center"
        height={32}
        borderRadius={1.5}
        bgcolor={backgroundColor}
        style={{
          cursor: (onClick || canRename) && !disableEditing ? 'pointer' : 'auto',
          userSelect: 'none',
          transition: 'all 150ms ease-in-out',
        }}
        onMouseEnter={() => !disableEditing && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={disableEditing || isRenaming ? undefined : handleClick}
      >
        <Stack direction="row" spacing={0.5} alignItems="center" minHeight={24}>
          {!!renderStartContent && renderStartContent({ isHovered, isRenaming, isHighlighted })}

          <Box flex={1} overflow={isRenaming ? 'visible' : 'hidden'}>
            <StyledValidationTooltip
              open={isFocused || isHovered}
              title={
                errorMessage ? (
                  <ValidationErrorMessage message={errorMessage} type={ValidationLCTypes.ERROR} />
                ) : (
                  ''
                )
              }
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Box
                  data-testid={NodeHeaderTestIds.NameContainer}
                  flex={1}
                  display="flex"
                  alignItems="center"
                  minHeight={36}
                  overflow={isRenaming ? 'visible' : 'hidden'}
                  whiteSpace="nowrap"
                  onDoubleClick={disableEditing || disabled ? undefined : handleLabelDoubleClick}
                >
                  {nodeName}
                </Box>

                {addItemIcon}
                {moreIconMenu}
              </Stack>
            </StyledValidationTooltip>
          </Box>

          {!!renderEndContent && renderEndContent({ isHovered, isRenaming, isHighlighted })}
        </Stack>
      </Stack>

      <Menu
        open={!!menuAnchor}
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        actions={menuActions}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </Fragment>
  );
};
